import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar,
  ChevronRight,
  Plus,
  X,
  Check,
  AlertCircle,
  Search
} from 'lucide-react';
import { 
  fetchClients, 
  fetchAllUpcomingSessions,
  addClientSession,
  updateSessionStatus
} from '../../lib/supabase';

const SessionsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [sessionTitle, setSessionTitle] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'today', 'week', 'month'

  // Generate time slots from 9 AM to 6 PM
  const timeSlots = Array.from({ length: 20 }, (_, i) => {
    const hour = Math.floor(i / 2) + 9;
    const minute = i % 2 === 0 ? '00' : '30';
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  });

  // Filter clients based on search term
  const filteredClients = clients.filter(client => 
    client.status !== 'cancelled' &&
    (client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
     client.email?.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
     client.phone?.includes(clientSearchTerm))
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clientsData, sessionsData] = await Promise.all([
        fetchClients(),
        fetchAllUpcomingSessions()
      ]);
      
      setClients(clientsData);
      setSessions(sessionsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleSession = async () => {
    if (!selectedClient || !selectedDate || !selectedTime || !sessionTitle.trim()) {
      setModalError('Please fill in all fields');
      return;
    }
  
    try {
      const sessionDateTime = new Date(`${selectedDate}T${selectedTime}`);
      await addClientSession({
        client_id: selectedClient.id,
        title: sessionTitle, // This will be mapped to session_type
        session_date: sessionDateTime.toISOString(),
        status: 'scheduled', // Using 'scheduled' status to match your schema
        duration_minutes: 60, // Default 60 minutes
        location: 'Online' // Default location
      });
      
      // Refresh data
      await loadData();
      
      // Reset form
      setSelectedClient(null);
      setSelectedDate(null);
      setSelectedTime(null);
      setSessionTitle('');
      setIsModalOpen(false);
      setModalError(null);
    } catch (err) {
      console.error('Error scheduling session:', err);
      setModalError('Failed to schedule session: ' + (err.message || 'Unknown error'));
    }
  };

  const handleMarkComplete = async (sessionId) => {
    try {
      await updateSessionStatus(sessionId, 'completed');
      await loadData(); // Refresh the sessions list
    } catch (err) {
      console.error('Error updating session status:', err);
    }
  };

  const getFilteredSessions = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekEnd = new Date(today);
    weekEnd.setDate(today.getDate() + 7);
    const monthEnd = new Date(today);
    monthEnd.setMonth(today.getMonth() + 1);
    
    switch (filterMode) {
      case 'today':
        return sessions.filter(session => {
          const sessionDate = new Date(session.session_date);
          return sessionDate >= today && sessionDate < new Date(today.getTime() + 86400000) && session.status === 'scheduled';
        });
      case 'week':
        return sessions.filter(session => {
          const sessionDate = new Date(session.session_date);
          return sessionDate >= today && sessionDate < weekEnd && session.status === 'scheduled';
        });
      case 'month':
        return sessions.filter(session => {
          const sessionDate = new Date(session.session_date);
          return sessionDate >= today && sessionDate < monthEnd && session.status === 'scheduled';
        });
      default:
        return sessions.filter(session => session.status === 'scheduled');
    }
  };

  const filteredSessions = getFilteredSessions().sort((a, b) => 
    new Date(a.session_date) - new Date(b.session_date)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Session Management</h1>
          <p className="text-gray-600">Schedule and manage counseling sessions</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => {
              setIsModalOpen(true);
            }}
            className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus size={20} className="mr-2" />
            Schedule New Session
          </button>
        </div>
      </div>

      {/* Filter controls */}
      <div className="bg-white rounded-lg shadow p-4 border border-teal-500">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <h2 className="text-lg font-semibold">Filter Sessions</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterMode('all')}
              className={`px-4 py-2 rounded-md ${
                filterMode === 'all' 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterMode('today')}
              className={`px-4 py-2 rounded-md ${
                filterMode === 'today' 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setFilterMode('week')}
              className={`px-4 py-2 rounded-md ${
                filterMode === 'week' 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => setFilterMode('month')}
              className={`px-4 py-2 rounded-md ${
                filterMode === 'month' 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              This Month
            </button>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-teal-500">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Upcoming Sessions</h2>
          
          {filteredSessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No upcoming sessions found for the selected filter
            </div>
          ) : (
            <div className="space-y-4">
              {filteredSessions.map(session => (
                <div 
                  key={session.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                  onClick={() => navigate(`/admin/dashboard/clients/${session.client_id}`)}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-teal-100 p-2 rounded-full">
                      <Calendar className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {session.clients?.name || 'Client'}
                      </h3>
                      <p className="text-sm text-teal-600 font-medium">{session.session_type}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(session.session_date).toLocaleString(undefined, {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkComplete(session.id);
                      }}
                      className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors flex items-center"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Complete
                    </button>
                    <div className="flex items-center text-teal-600">
                      <span className="text-sm mr-1">View Details</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Schedule Session Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full relative z-10">
            <button
              onClick={() => {
                setIsModalOpen(false);
                setModalError(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
            >
              <X size={20} />
            </button>

            <h2 className="text-xl font-semibold mb-4">
              Schedule New Session
            </h2>

            {modalError && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                {modalError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session Title
                </label>
                <input
                  type="text"
                  value={sessionTitle}
                  onChange={(e) => setSessionTitle(e.target.value)}
                  placeholder="e.g., Initial Assessment, Follow-up Session"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Client
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
                  />
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                  {filteredClients.length > 0 ? (
                    filteredClients.map(client => (
                      <div
                        key={client.id}
                        onClick={() => {
                          setSelectedClient(client);
                          setClientSearchTerm('');
                        }}
                        className={`p-2 cursor-pointer hover:bg-gray-50 ${
                          selectedClient?.id === client.id ? 'bg-teal-50' : ''
                        }`}
                      >
                        <div className="font-medium text-gray-900">{client.name}</div>
                        <div className="text-sm text-gray-500">{client.email}</div>
                        {client.phone && (
                          <div className="text-sm text-gray-500">{client.phone}</div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-gray-500 text-sm">
                      No clients found matching your search
                    </div>
                  )}
                </div>
                {selectedClient && (
                  <div className="mt-2 p-2 bg-teal-50 rounded-md">
                    <div className="text-sm font-medium text-teal-800">Selected Client:</div>
                    <div className="text-sm text-teal-700">{selectedClient.name}</div>
                    <button
                      onClick={() => {
                        setSelectedClient(null);
                        setClientSearchTerm('');
                      }}
                      className="mt-1 text-xs text-teal-600 hover:text-teal-800"
                    >
                      Change Selection
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Date
                </label>
                <input
                  type="date"
                  value={selectedDate || ''}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Time
                </label>
                <select
                  value={selectedTime || ''}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
                >
                  <option value="">Select a time...</option>
                  {timeSlots.map(time => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setModalError(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleSession}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center"
              >
                <Check size={20} className="mr-2" />
                Schedule Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsPage;